import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler, cleNeuve } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Carte, Erreur } from '../../composants/communs.tsx';
import { LignesArticles, entierPositif, type LigneSaisie } from './LignesArticles.tsx';

/**
 * Envoi de marchandise vers une autre boutique — RG-050, RG-051.
 *
 * La marchandise QUITTE la source dès l'envoi : elle n'est plus vendable
 * ici, et ne le sera là-bas qu'à la réception. Entre les deux, elle est « en
 * route », et c'est voulu : un camion n'est ni dans une boutique ni dans
 * l'autre.
 */
export function FormulaireEnvoi({ onFerme }: { onFerme: () => void }): ReactNode {
  const { boutiqueId, boutiques } = useSession();
  const client = useQueryClient();

  const [identite] = useState(() => ({ id: cleNeuve(), cle: cleNeuve() }));
  const [source, setSource] = useState<string>(boutiqueId ?? boutiques[0]?.id ?? '');
  const [destination, setDestination] = useState('');
  const [motif, setMotif] = useState('');
  const [lignes, setLignes] = useState<LigneSaisie[]>([]);

  // Toutes les boutiques actives : la destination sort de la portée de
  // l'expéditeur, sinon aucun envoi entre deux sites ne serait possible.
  const { data: destinations } = useQuery({
    queryKey: ['destinations'],
    queryFn: () =>
      appeler<{ donnees: { id: string; code: string; nom: string }[] }>('/transferts/destinations'),
    select: (reponse) => reponse.donnees,
  });
  const choix = (destinations ?? []).filter((boutique) => boutique.id !== source);

  const complet =
    source !== '' &&
    destination !== '' &&
    destination !== source &&
    lignes.length > 0 &&
    lignes.every((ligne) => entierPositif(ligne.quantite) !== null);

  const envoi = useMutation({
    mutationFn: () =>
      appeler<{ numero: string }>('/transferts', {
        methode: 'POST',
        cleIdempotence: identite.cle,
        corps: {
          id: identite.id,
          boutique_source_id: source,
          boutique_destination_id: destination,
          motif: motif.trim() === '' ? null : motif.trim(),
          lignes: lignes.map((ligne) => ({
            produit_id: ligne.produitId,
            quantite: Number(ligne.quantite),
          })),
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['transferts'] });
      void client.invalidateQueries({ queryKey: ['produits'] });
      onFerme();
    },
  });

  return (
    <Carte
      titre="Nouvel envoi"
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Annuler
        </button>
      }
    >
      <div className="champs">
        <div className="champ">
          <label htmlFor="envoi-source">Depuis</label>
          <select
            id="envoi-source"
            value={source}
            onChange={(evenement) => {
              setSource(evenement.target.value);
              setLignes([]); // Le stock affiché dépend de la boutique source.
              if (destination === evenement.target.value) {
                setDestination('');
              }
            }}
          >
            {boutiques.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="envoi-destination">Vers</label>
          <select
            id="envoi-destination"
            value={destination}
            onChange={(evenement) => {
              setDestination(evenement.target.value);
            }}
          >
            <option value="">Choisir la boutique destinataire</option>
            {choix.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="envoi-motif">Motif (facultatif)</label>
          <input
            id="envoi-motif"
            maxLength={500}
            value={motif}
            onChange={(evenement) => {
              setMotif(evenement.target.value);
            }}
          />
        </div>
      </div>

      <LignesArticles
        boutiqueId={source === '' ? null : source}
        lignes={lignes}
        onChange={setLignes}
        avecCout={false}
      />

      <Erreur erreur={envoi.error} />
      <div className="ligne" style={{ marginTop: 12 }}>
        <button
          type="button"
          disabled={!complet || envoi.isPending}
          onClick={() => {
            envoi.mutate();
          }}
        >
          {envoi.isPending ? 'Envoi…' : 'Expédier'}
        </button>
      </div>
    </Carte>
  );
}
